import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { ApiError } from '@/lib/apiClient'
import { formatDateTime } from '@/lib/datetime'
import { cn } from '@/lib/cn'

import { useAdminMutation } from '../api'
import { useAdminContext } from '../session'
import type { AdminOrder, AdminSupplement } from '../types'
import { MoneyField, SelectField, TextField } from './fields'

/** Les mêmes moyens que l'encaissement d'une commande (`OrdersPage`). */
const OFFLINE_METHODS = ['paypal', 'transfer', 'cash', 'manual']

/**
 * Les plats ajoutés à une commande — un lot par passage en caisse.
 *
 * Chaque lot a son propre argent : on l'encaisse, le renvoie, l'annule ou le
 * rembourse **ici**, sans jamais toucher à la commande elle-même. Une table
 * réglée reste réglée pendant qu'un supplément attend le sien — c'est tout
 * l'intérêt d'un numéro unique sans mélanger les montants.
 */
export function OrderSupplements({
  order,
  eventId,
  currency,
  money,
}: {
  order: AdminOrder
  eventId: string | undefined
  currency: string
  money: (minor: number | null | undefined) => string
}) {
  const { t } = useTranslation()
  const supplements = order.supplements ?? []
  if (supplements.length === 0) return null

  return (
    <section className="mt-6 border-t border-line-subtle pt-5">
      <p className="text-caption uppercase tracking-wide text-content-muted">
        {t('admin.orders.supplements.title')}
      </p>
      <p className="mt-1 text-xs text-content-muted">{t('admin.orders.supplements.hint')}</p>
      <ul className="mt-3 space-y-3">
        {supplements.map((row) => (
          <li key={row.id}>
            <SupplementRow
              order={order}
              supplement={row}
              eventId={eventId}
              currency={currency}
              money={money}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}

type Gesture = 'pay' | 'cancel' | 'refund' | null

function SupplementRow({
  order,
  supplement,
  eventId,
  currency,
  money,
}: {
  order: AdminOrder
  supplement: AdminSupplement
  eventId: string | undefined
  currency: string
  money: (minor: number | null | undefined) => string
}) {
  const { t, i18n } = useTranslation()
  const { event } = useAdminContext()
  const [gesture, setGesture] = useState<Gesture>(null)
  const [method, setMethod] = useState(OFFLINE_METHODS[0] ?? 'cash')
  const [reference, setReference] = useState('')
  const [reason, setReason] = useState('')
  const [amount, setAmount] = useState<number | null>(null)

  const base = `/admin/events/${eventId}/orders/${order.id}/supplements/${supplement.id}`
  const invalidate = [['admin-orders', eventId], ['admin-readiness', eventId ?? '']]
  const markPaid = useAdminMutation<AdminOrder, { method: string; reference: string | null }>(
    'post',
    () => `${base}/mark-paid`,
    eventId,
    invalidate,
  )
  const cancel = useAdminMutation<AdminOrder, { reason: string }>(
    'post',
    () => `${base}/cancel`,
    eventId,
    invalidate,
  )
  const refund = useAdminMutation<unknown, { amount_minor: number; reason: string }>(
    'post',
    () => `${base}/refund`,
    eventId,
    invalidate,
  )
  const sendLink = useAdminMutation<{ message: string }, Record<string, never>>(
    'post',
    () => `${base}/send-payment-link`,
    eventId,
    invalidate,
  )
  const busy = markPaid.isPending || cancel.isPending || refund.isPending || sendLink.isPending
  const error = markPaid.error ?? cancel.error ?? refund.error ?? sendLink.error
  const refundable = Math.max((supplement.paid_minor ?? 0) - (supplement.refunded_minor ?? 0), 0)
  // Nuls : le serveur a retiré les montants (la porte, sans `orders.amount.read`).
  const hidden = supplement.total_minor === null
  const when = (iso: string) => formatDateTime(iso, i18n.language, event?.timezone ?? 'UTC')
  const dead = ['cancelled', 'expired', 'refunded'].includes(supplement.status)
  const done = () => {
    setGesture(null)
    setReason('')
    setReference('')
    setAmount(null)
  }

  return (
    <div
      className={cn(
        'rounded-lg border px-4 py-3',
        supplement.refund_due
          ? 'border-state-danger/40 bg-state-danger/5'
          : supplement.status === 'pending_payment'
            ? 'border-ember-500/40'
            : supplement.status === 'paid'
              ? 'border-state-success/40 bg-state-success/5'
              : 'border-line-subtle',
        dead && 'opacity-70',
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-sm text-content-primary">
          {t('admin.orders.supplements.label', { sequence: supplement.sequence })}
          {hidden ? null : (
            <>
              {' · '}
              <span className="tabular-nums">{money(supplement.total_minor)}</span>
            </>
          )}
        </p>
        <span className="text-xs text-content-muted">
          {t(`admin.orders.status.${supplement.status}`)}
          {supplement.payment_reference ? ` · ${supplement.payment_reference}` : ''}
        </span>
      </div>
      {supplement.merged_from_number ? (
        <p className="mt-0.5 text-xs text-content-muted">
          {t('admin.orders.supplements.mergedFrom', { number: supplement.merged_from_number })}
        </p>
      ) : null}

      <ul className="mt-2 space-y-0.5 text-sm">
        {supplement.items.map((item, index) => (
          <li key={`${item.label}-${index}`} className="flex justify-between gap-4">
            <span className="min-w-0 break-words text-content-secondary">
              {item.quantity}× {item.label}
            </span>
            {item.line_total_minor === null ? null : (
              <span className="shrink-0 tabular-nums text-content-muted">
                {money(item.line_total_minor)}
              </span>
            )}
          </li>
        ))}
      </ul>

      <p className="mt-2 text-xs text-content-muted">
        {supplement.status === 'pending_payment'
          ? [
              hidden
                ? t('admin.orders.supplements.dueHidden')
                : t('admin.orders.supplements.due', { amount: money(supplement.amount_due_minor) }),
              supplement.payment_choice === 'cash_at_door' ||
              supplement.payment_choice === 'paypal_at_door'
                ? t('admin.orders.supplements.onSite')
                : supplement.due_at
                  ? t('admin.orders.dueOn', { date: when(supplement.due_at) })
                  : null,
            ]
              .filter(Boolean)
              .join(' · ')
          : supplement.paid_at
            ? t('admin.orders.paidOn', { date: when(supplement.paid_at) })
            : supplement.cancel_reason ?? ''}
        {(supplement.refunded_minor ?? 0) > 0
          ? ` · ${t('admin.orders.refunded')} ${money(supplement.refunded_minor)}`
          : ''}
      </p>
      {supplement.refund_due && !hidden ? (
        <p className="mt-1 text-xs text-state-danger">
          {t('admin.orders.supplements.refundDue', { amount: money(refundable) })}
        </p>
      ) : null}

      {error instanceof ApiError ? (
        <p className="mt-2 text-sm text-state-danger">{error.message}</p>
      ) : null}

      {gesture === 'pay' ? (
        <div className="mt-3 space-y-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              label={t('admin.orders.method')}
              value={method}
              onChange={setMethod}
              options={OFFLINE_METHODS.map((value) => ({
                value,
                label: t(`admin.orders.methods.${value}`),
              }))}
            />
            <TextField
              label={
                method === 'paypal'
                  ? t('admin.orders.referencePaypal')
                  : t('admin.orders.reference')
              }
              value={reference}
              onChange={setReference}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={busy}
              onClick={() =>
                markPaid.mutate(
                  { method, reference: reference.trim() || null },
                  { onSuccess: done },
                )
              }
            >
              {hidden
                ? t('admin.orders.confirmPaid')
                : t('admin.orders.supplements.confirmPaid', {
                    amount: money(supplement.amount_due_minor),
                  })}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setGesture(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      ) : gesture === 'cancel' ? (
        <div className="mt-3 space-y-3">
          <TextField
            label={t('admin.orders.cancelReason')}
            hint={t('admin.orders.supplements.cancelHint')}
            value={reason}
            onChange={setReason}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={busy || reason.trim().length < 3}
              onClick={() => cancel.mutate({ reason: reason.trim() }, { onSuccess: done })}
            >
              {t('admin.orders.supplements.confirmCancel')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setGesture(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      ) : gesture === 'refund' ? (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-content-muted">
            {t('admin.orders.refundable', { amount: money(refundable) })}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <MoneyField
              label={t('admin.orders.refundAmount')}
              minor={amount}
              currency={currency}
              onChange={setAmount}
            />
            <TextField
              label={t('admin.orders.refundReason')}
              hint={t('admin.orders.refundReasonHint')}
              value={reason}
              onChange={setReason}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={
                busy ||
                amount === null ||
                amount <= 0 ||
                amount > refundable ||
                reason.trim().length < 5
              }
              onClick={() =>
                refund.mutate(
                  { amount_minor: amount ?? 0, reason: reason.trim() },
                  { onSuccess: done },
                )
              }
            >
              {t('admin.orders.confirmRefund')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setGesture(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {supplement.payable ? (
            <Button size="sm" disabled={busy} onClick={() => setGesture('pay')}>
              {t('admin.orders.supplements.markPaid')}
            </Button>
          ) : null}
          {supplement.payable && supplement.payment_link ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => sendLink.mutate({})}
            >
              {sendLink.isSuccess
                ? t('admin.orders.sendLinkAgain')
                : t('admin.orders.supplements.sendLink')}
            </Button>
          ) : null}
          {supplement.cancellable ? (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setGesture('cancel')}>
              {t('admin.orders.supplements.cancel')}
            </Button>
          ) : null}
          {supplement.refundable ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setGesture('refund')}>
              {t('admin.orders.supplements.refund')}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  )
}

/**
 * « Fusionner dans la commande d'entrée » — le cas réel du propriétaire.
 *
 * Une commande de plats d'avant les suppléments, liée à une entrée : un client
 * avec deux numéros. Le geste en fait un seul. L'argent ne bouge pas — les
 * paiements passent tels quels dans un supplément de l'entrée — et cette
 * commande-ci reste, close, avec la mention « fusionnée dans … ».
 */
export function MergeIntoEntry({
  order,
  eventId,
  onMerged,
}: {
  order: AdminOrder
  eventId: string | undefined
  onMerged: (entry: AdminOrder) => void
}) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)
  // Coché d'office : le client a reçu deux numéros, il doit savoir lequel garder.
  const [notify, setNotify] = useState(true)
  const merge = useAdminMutation<AdminOrder, { notify: boolean }>(
    'post',
    () => `/admin/events/${eventId}/orders/${order.id}/merge`,
    eventId,
    [['admin-orders', eventId], ['admin-readiness', eventId ?? '']],
  )

  if (order.merged_into_order_number) {
    return (
      <p className="mt-6 rounded-md border border-line-subtle px-3 py-2 text-sm text-content-secondary">
        {t('admin.orders.merge.done', { number: order.merged_into_order_number })}{' '}
        <Link
          to={`?q=${encodeURIComponent(order.merged_into_order_number)}`}
          className="text-champagne-300 underline"
        >
          {t('admin.orders.merge.open')}
        </Link>
      </p>
    )
  }
  if (!order.mergeable || !order.admission_order_number) return null

  return (
    <section className="mt-6 border-t border-line-subtle pt-5">
      <p className="text-caption uppercase tracking-wide text-content-muted">
        {t('admin.orders.merge.title')}
      </p>
      <p className="mt-1 text-xs text-content-muted">
        {t('admin.orders.merge.hint', { number: order.admission_order_number })}
      </p>
      {merge.error instanceof ApiError ? (
        <p className="mt-2 text-sm text-state-danger">{merge.error.message}</p>
      ) : null}
      {confirming ? (
        <div className="mt-3 space-y-3">
          <label className="flex items-start gap-3 rounded-md border border-line-subtle px-3 py-2.5">
            <input
              type="checkbox"
              checked={notify}
              onChange={() => setNotify((value) => !value)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--ember-500)]"
            />
            <span className="min-w-0 text-sm text-content-primary">
              {t('admin.orders.merge.notify')}
              {order.customer_email ? (
                <span className="block break-words text-xs text-content-muted">
                  {order.customer_email}
                </span>
              ) : null}
            </span>
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={merge.isPending}
              onClick={() => merge.mutate({ notify }, { onSuccess: onMerged })}
            >
              {t('admin.orders.merge.confirm', { number: order.admission_order_number })}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <Button size="sm" variant="outline" className="mt-3" onClick={() => setConfirming(true)}>
          {t('admin.orders.merge.action')}
        </Button>
      )}
    </section>
  )
}
